import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './Dialogues.css';
import './finishScreen.css';
import css from "../lesson.module.css";
import '../finishedScreen.css';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import WebSocketManager from '../../../lib/websocketManager';
import { trackAIDialogueCompleted, trackLessonOpened, trackLessonCompleted } from '../../../lib/analytics';
import { checkDialogScriptExists } from '../../../lib/dialogScriptApi';
import StruggleSupportModal from './StruggleSupportModal';
import againIcon from '../../../img/arrow-rotate-right-solid-full.svg';
import translateIcon from '../../../img/language-solid-full.svg';
import snailIcon from '../../../img/snail.png';
import NotCompletionScreen from '../FailedLessonScreen';

const Dialogues = ({ lesson, authUser, onActivityComplete }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Sample data with 5 words per topic
  const topics = [
    {
      id: 1,
      title: "Tech & Style",
      description: "Where innovation meets aesthetic - discuss tech with flair",
      vocabulary: ["innovation", "seamless", "intuitive", "sophisticated", "transformative"],
      icon: "💻"
    },
    {
      id: 2,
      title: "Travel & Lifestyle",
      description: "Explore the world with taste and sophistication",
      vocabulary: ["wanderlust", "authentic", "immersive", "luxurious", "breathtaking"],
      icon: "✈️"
    },
    {
      id: 3,
      title: "Wellness & Beauty",
      description: "The art of living well - mind, body, and soul",
      vocabulary: ["mindfulness", "holistic", "rejuvenating", "balance", "vitality"],
      icon: "🌸"
    }
  ];

  // Example answers for each topic
  const exampleAnswers = {
    "Tech & Style": [
      "I find the innovation in AI particularly fascinating, especially how it creates such seamless experiences in our daily lives.",
      "The most intuitive technology I've used recently is voice assistants - they've become so sophisticated and transformative.",
      "What excites me is how innovation in design makes technology feel more intuitive and accessible to everyone.",
      "I love how seamless integration between devices has made our digital lives more sophisticated and efficient.",
      "The transformative power of technology amazes me, especially when it's designed with such intuitive interfaces."
    ],
    "Travel & Lifestyle": [
      "My wanderlust always leads me to seek authentic experiences, especially in places with immersive cultural traditions.",
      "I recently had a luxurious stay in Bali that was absolutely breathtaking - the authentic local cuisine was incredible.",
      "Travel feeds my wanderlust because each destination offers such immersive and authentic cultural experiences.",
      "The most breathtaking moment was watching the sunset from a luxurious resort overlooking the Mediterranean.",
      "I prefer authentic local experiences over touristy spots - they're more immersive and truly breathtaking."
    ],
    "Wellness & Beauty": [
      "I practice mindfulness through daily meditation, which brings such balance and vitality to my life.",
      "My holistic approach to wellness includes both rejuvenating physical exercise and mindfulness practices.",
      "Finding balance through holistic wellness practices has been truly rejuvenating for my overall health.",
      "Mindfulness meditation gives me incredible vitality and helps maintain balance in my busy life.",
      "I believe in a holistic approach to health that combines rejuvenating activities with proper nutrition for vitality."
    ]
  };

  // State management
  const [showReadyScreen, setShowReadyScreen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [selectedTopic] = useState(topics[0]); // Default topic
  const [currentLessonId, setCurrentLessonId] = useState(lesson?.id || null);
  const [isRecording, setIsRecording] = useState(false);
  const [messages, setMessages] = useState([]);
  const [errorContent, setErrorContent] = useState(null); // Store error info: { message, code, title }
  const [usedVocabulary, setUsedVocabulary] = useState(new Set());
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [totalTimeInvested, setTotalTimeInvested] = useState(0);
  const [turnCount, setTurnCount] = useState(0);
  const [isLessonCompleted, setIsLessonCompleted] = useState(false);
  const [lastAIQuestion, setLastAIQuestion] = useState("");
  const [showCompletion, setshowCompletion] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  const [showUserHelp, setShowUserHelp] = useState(false);
  const [helpExpanded, setHelpExpanded] = useState(false);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [wsStatus, setWsStatus] = useState('disconnected'); // disconnected, connecting, connected, error
  const [currentStep, setCurrentStep] = useState(0);
  const [totalSteps, setTotalSteps] = useState(0);
  const [sessionId, setSessionId] = useState(null);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [offTopicCount, setOffTopicCount] = useState(0);
  const [translatedMessages, setTranslatedMessages] = useState({}); // Store translations by message ID
  const [translatingMessages, setTranslatingMessages] = useState(new Set()); // Track which messages are being translated
  const [currentHint, setCurrentHint] = useState(null); // Store current hint from backend
  const [recordedAudioBlob, setRecordedAudioBlob] = useState(null); // Store recorded audio blob
  const [recordingDuration, setRecordingDuration] = useState(0); // Recording duration in seconds

  // --- Design 29 State ---
  const [feedbackBox, setFeedbackBox] = useState(null); // { type: 'correct'|'wrong', expected?, actual?, explanation? }
  const [nextAiMessage, setNextAiMessage] = useState(null);
  const [nextAiAudioUrl, setNextAiAudioUrl] = useState(null);
  const [isWaitingForNext, setIsWaitingForNext] = useState(false);
  const [userAnswer, setUserAnswer] = useState(null); // The text the user just said
  const [speakingState, setSpeakingState] = useState('idle'); // 'idle' | 'recording' | 'preview'

  const [recordingStartTime, setRecordingStartTime] = useState(null); // When recording started
  const [isPlayingRecording, setIsPlayingRecording] = useState(false); // Is recorded audio playing
  const [audioWaveformData, setAudioWaveformData] = useState([]); // Waveform amplitude data
  const [playbackProgress, setPlaybackProgress] = useState(0); // Playback progress (0-1)
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState(0); // Current playback time in seconds
  const [audioDuration, setAudioDuration] = useState(0); // Total audio duration in seconds
  const recordedAudioRef = useRef(null); // Audio element for playing recorded audio
  const playbackIntervalRef = useRef(null); // Interval for updating playback progress
  const playbackAnimationFrameRef = useRef(null); // Animation frame for updating playback progress
  const silenceDetectionTimeoutRef = useRef(null); // Timeout for detecting user silence (no response)

  // Videos state for results calculation
  const [videos, setVideos] = useState(authUser?.videoLessonsAll || []);

  // Collect mistakes during the session
  const [sessionMistakes, setSessionMistakes] = useState([]);
  // IDs of correction messages user has already skipped (hide skip button)
  const [skippedCorrectionIds, setSkippedCorrectionIds] = useState(() => new Set());

  // Struggle support modal state
  const [showStruggleModal, setShowStruggleModal] = useState(false);
  const [struggleVariant, setStruggleVariant] = useState('errors'); // 'errors' or 'silence'
  const [consecutiveErrors, setConsecutiveErrors] = useState(0);
  const [consecutiveSilences, setConsecutiveSilences] = useState(0);
  const STRUGGLE_THRESHOLD = 5; // Show modal after 5 consecutive errors or silences

  // Results state
  const [lessonResults, setLessonResults] = useState({
    score: 75,
    correct: 6,
    wrong: 3,
    newWords: 8,
    mistakes: []
  });

  // Refs for animations
  const scoreNumberRef = useRef(null);
  const scoreRingRef = useRef(null);
  const confettiContainerRef = useRef(null);
  const correctCountRef = useRef(null);
  const wrongCountRef = useRef(null);
  const wordsCountRef = useRef(null);
  const xpBarFillRef = useRef(null);

  // Refs
  const messagesContainerRef = useRef(null);
  const recognitionRef = useRef(null);
  const wsManagerRef = useRef(null);
  const audioChunksRef = useRef([]); // For AI audio playback
  const audioContextRef = useRef(null);
  const currentAudioRef = useRef(null);
  const sessionStartedRef = useRef(false); // Prevent multiple session starts
  const messageAudioUrlsRef = useRef({}); // Store audio URLs for each message ID
  const recordingTimerRef = useRef(null); // Timer for recording duration
  const recordedChunksRef = useRef([]); // Store audio chunks for playback
  const ukTtsVoiceRef = useRef(null);
  const lastCorrectionMessageIdRef = useRef(null); // Track last correction message ID for audio pairing
  const lastErrorCheckTimeRef = useRef(0); // Prevent duplicate modal triggers
  const justReceivedCorrectionRef = useRef(false); // Track if we just received a correction

  // --- Design 29 Refs ---
  const isWaitingForNextRef = useRef(false);
  const nextAiMessageRef = useRef(null);
  const nextAiAudioChunksRef = useRef([]);
  const userAnswerRef = useRef(null);

  // Set videos from authUser when component mounts or authUser changes
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;
      recognitionRef.current.lang = 'en-US';
    }

    // Cleanup on unmount
    return () => {
      cleanupRecording();
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
        processingTimeoutRef.current = null;
      }
      if (playbackIntervalRef.current) {
        clearInterval(playbackIntervalRef.current);
        playbackIntervalRef.current = null;
      }
      if (playbackAnimationFrameRef.current) {
        cancelAnimationFrame(playbackAnimationFrameRef.current);
        playbackAnimationFrameRef.current = null;
      }
      if (silenceDetectionTimeoutRef.current) {
        clearTimeout(silenceDetectionTimeoutRef.current);
        silenceDetectionTimeoutRef.current = null;
      }
      if (wsManagerRef.current) {
        wsManagerRef.current.close();
      }
      
      // Clean up audio URLs
      Object.values(messageAudioUrlsRef.current).forEach(url => {
        URL.revokeObjectURL(url);
      });
      messageAudioUrlsRef.current = {};
    };
  }, []);

  // Auto-start the lesson (skip the welcome modal)
  useEffect(() => {
    if (lesson?.id && !isInitializing && wsStatus === 'disconnected' && !showReadyScreen) {
      startLesson();
    }
  }, [lesson?.id]);

  // Pick best Ukrainian voice (prioritize uk-UA for proper ї, є pronunciation)
  const pickUkrainianVoice = (voices) => {
    if (!voices || !voices.length) return null;
    const isUkrainianLang = (v) => v.lang && (v.lang === 'uk-UA' || v.lang === 'uk_UA' || v.lang.toLowerCase().startsWith('uk'));
    const isRussianLang = (v) => v.lang && v.lang.toLowerCase().startsWith('ru');
    const hasUkrainianInName = (v) => /ukrain|україн|Polina|Ostap|Lesya|ukrainian|uk-UA|українськ/i.test(v.name || '');
    // Prefer: 1) uk-UA exactly, 2) any uk-* 3) name contains Ukrainian indicators; exclude Russian
    const ukVoices = voices.filter(v => isUkrainianLang(v) && !isRussianLang(v));
    return (
      ukVoices.find(v => v.lang === 'uk-UA' || v.lang === 'uk_UA') ||
      ukVoices.find(v => hasUkrainianInName(v)) ||
      ukVoices[0] ||
      voices.find(v => hasUkrainianInName(v) && !isRussianLang(v)) ||
      voices.find(v => isUkrainianLang(v)) ||
      null
    );
  };

  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      const loadVoices = () => {
        try {
          const voices = window.speechSynthesis.getVoices() || [];
          const ukVoice = pickUkrainianVoice(voices);
          if (ukVoice) {
            ukTtsVoiceRef.current = ukVoice;
          }
        } catch (_) {}
      };
      loadVoices();
      window.speechSynthesis.addEventListener('voiceschanged', loadVoices);
      return () => {
        window.speechSynthesis.removeEventListener('voiceschanged', loadVoices);
      };
    }
  }, []);

  const getUkrainianVoice = async () => {
    if (!('speechSynthesis' in window)) return null;
    const pickVoice = () => pickUkrainianVoice(window.speechSynthesis.getVoices() || []);
    if (ukTtsVoiceRef.current) return ukTtsVoiceRef.current;
    let voice = pickVoice();
    if (voice) {
      ukTtsVoiceRef.current = voice;
      return voice;
    }
    // Wait for voices to load (browsers load asynchronously)
    voice = await new Promise(resolve => {
      let resolved = false;
      const handler = () => {
        if (resolved) return;
        const v = pickVoice();
        if (v) {
          resolved = true;
          window.speechSynthesis.removeEventListener('voiceschanged', handler);
          resolve(v);
        }
      };
      window.speechSynthesis.addEventListener('voiceschanged', handler);
      try { window.speechSynthesis.getVoices(); } catch (_) {}
      setTimeout(() => {
        if (!resolved) {
          window.speechSynthesis.removeEventListener('voiceschanged', handler);
          resolve(pickVoice()); // Try once more before giving up
        }
      }, 2500);
    });
    if (voice) ukTtsVoiceRef.current = voice;
    return voice;
  };

  // Handle lesson changes - reset state when lesson changes
  useEffect(() => {
    // Set videos from authUser if available
    if (authUser?.videoLessonsAll) {
      setVideos(authUser.videoLessonsAll);
    }
    if (lesson?.id && lesson.id !== currentLessonId) {
      console.log('Lesson changed from', currentLessonId, 'to', lesson.id);

      // Close existing WebSocket connection
      if (wsManagerRef.current) {
        wsManagerRef.current.close();
        wsManagerRef.current = null;
      }

      // Reset ALL state for new lesson
      setCurrentLessonId(lesson.id);
      setMessages([]);
      setUsedVocabulary(new Set());
      setTurnCount(0);
      setTotalTimeInvested(0);
      setSessionStartTime(null);
      setIsLessonCompleted(false);
      setShowUserHelp(false);
      setHelpExpanded(false);
      setshowCompletion(false);
      setShowSummaryModal(false);
      setLastAIQuestion("");
      setIsSendingMessage(false);
      setWsStatus('disconnected');
      setCurrentStep(0);
      setTotalSteps(0);
      setSessionId(null);
      setHintsUsed(0);
      setOffTopicCount(0);
      setSessionMistakes([]); // Reset mistakes for new lesson
      setSkippedCorrectionIds(new Set());
      setErrorContent(null); // Clear any error messages
      sessionStartedRef.current = false; // Reset session started flag

      // Reset UI state
      setShowReadyScreen(false);
      setCurrentHint(null); // Clear any hints
      setHelpExpanded(false);
    }
  }, [lesson?.id, currentLessonId, authUser]);

  // Error handling functions
  const handleErrorAction = () => {
    // Reload the page
    window.location.reload();
  };

  const goToPricing = () => {
    // Navigate to pricing page
    navigate('/pricing');
  };

  // WebSocket message handler
  const handleWebSocketMessage = (data) => {
    console.log('📨 WebSocket message received:', data.type, data);

    switch (data.type) {
      case 'demo.info':
        // Demo mode information
        console.log('ℹ️ Demo mode:', data.remaining_dialogs, 'remaining out of', data.limit);
        // You can show this info to the user if needed
        break;

      case 'status.update':
        setWsStatus(data.status);
        if (data.status === 'connected') {
          console.log('✅ WebSocket connected successfully');
          // Start session only after connection is confirmed and if not already started
          if (wsManagerRef.current && wsManagerRef.current.isReady() && !sessionStartedRef.current) {
            console.log('🚀 Starting session...');
            sessionStartedRef.current = true;
            wsManagerRef.current.startSession('manual', 'default', true);
          }
        }
        break;

      case 'session.started':
        setSessionId(data.session_id);
        setTotalSteps(data.total_steps || 0);
        setCurrentStep(0);
        setSessionStartTime(new Date());
        setIsSendingMessage(false);
        setIsInitializing(false);
        setShowReadyScreen(false);
        sessionStartedRef.current = true; // Mark as started
        console.log('🎬 Session started:', data.session_id);
        break;

      case 'ai.response_text':
        // Check if we need to show positive feedback
        if (!justReceivedCorrectionRef.current && userAnswerRef.current) {
          setFeedbackBox({ type: 'correct' });
          isWaitingForNextRef.current = true;
          setIsWaitingForNext(true);
        }
        
        // Reset flags for the next turn
        justReceivedCorrectionRef.current = false;
        userAnswerRef.current = null;

        if (isWaitingForNextRef.current) {
          nextAiMessageRef.current = data;
          setNextAiMessage(data);
          return; // Buffer the message
        }

        // AI is about to speak - show text first
        const aiTextMessage = {
          id: Date.now(),
          sender: 'ai',
          text: data.text,
          timestamp: new Date(),
          hasAudio: true // Mark that this message will have audio
        };
        setMessages(prev => [...prev, aiTextMessage]);
        setLastAIQuestion(data.text);
        setShowUserHelp(true);
        setCurrentStep(data.step || currentStep);
        break;

      case 'ai.audio_chunk':
        if (isWaitingForNextRef.current) {
          nextAiAudioChunksRef.current.push(data);
          return; // Buffer the chunk
        }
        // Handle audio chunk for playback
        handleAudioChunk(data);
        break;

      case 'ai.audio_complete':
        // Audio playback complete
        console.log('🔊 AI audio complete');
        setIsSendingMessage(false);
        break;

      case 'ai.transcript_partial':
        // Show partial transcript (optional - for real-time feedback)
        console.log('📝 Partial transcript:', data.text);
        break;

      case 'ai.transcript_final':
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = null;
        }
        
        const userMessage = {
          id: Date.now(),
          sender: 'user',
          text: data.text,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, userMessage]);
        setUserAnswer(data.text);
        userAnswerRef.current = data.text; // Store in ref for immediate access
        setTurnCount(prev => prev + 1);
        setShowUserHelp(false);
        setIsSendingMessage(false);
        setConsecutiveSilences(0);
        
        // Check for vocabulary usage
        selectedTopic.vocabulary.forEach(word => {
          const regex = new RegExp(`\\b${word}\\b`, 'gi');
          if (regex.test(data.text)) {
            markVocabularyUsed(word);
          }
        });
        break;

      case 'ai.correction':
        const correctionMsg = {
          id: Date.now(),
          sender: 'ai',
          text: data.expected,
          explanation: data.message,
          timestamp: new Date(),
          isCorrection: true,
          expected: data.expected,
          actual: data.actual,
          hasAudio: false
        };
        lastCorrectionMessageIdRef.current = correctionMsg.id;
        setMessages(prev => [...prev, correctionMsg]);
        
        setFeedbackBox({ type: 'wrong', expected: data.expected, actual: data.actual, explanation: data.message });
        isWaitingForNextRef.current = true;
        setIsWaitingForNext(true);
        justReceivedCorrectionRef.current = true;
        
        const mistake = {
          studentAnswer: data.actual,
          correctAnswer: data.expected,
          explanation: data.message,
          type: 'Граматика'
        };
        setSessionMistakes(prev => [...prev, mistake]);
        
        // Track consecutive errors
        setConsecutiveErrors(prev => {
          const newCount = prev + 1;
          if (newCount >= STRUGGLE_THRESHOLD) {
            const now = Date.now();
            if (now - lastErrorCheckTimeRef.current > 5000) {
              lastErrorCheckTimeRef.current = now;
              setStruggleVariant('errors');
              setShowStruggleModal(true);
            }
          }
          return newCount;
        });
        break;

      case 'ai.correction_audio':
        // Store audio for the correction message using the tracked ID
        const correctionMessageId = lastCorrectionMessageIdRef.current;
        if (correctionMessageId) {
          // Decode base64 audio
          try {
            const binaryString = atob(data.data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            const mimeType = data.format === 'mp3' ? 'audio/mpeg' : `audio/${data.format}`;
            const blob = new Blob([bytes], { type: mimeType });
            const audioUrl = URL.createObjectURL(blob);
            
            // Store audio URL for this correction message
            messageAudioUrlsRef.current[correctionMessageId] = audioUrl;
            
            // Mark message as having audio
            setMessages(prev => {
              const updated = [...prev];
              const messageIndex = updated.findIndex(m => m.id === correctionMessageId);
              if (messageIndex !== -1) {
                updated[messageIndex] = {
                  ...updated[messageIndex],
                  hasAudio: true
                };
              }
              return updated;
            });
            
            console.log('🔊 Correction audio stored for message:', correctionMessageId);
            lastCorrectionMessageIdRef.current = null; // Clear after use
          } catch (error) {
            console.error('Error processing correction audio:', error);
          }
        }
        break;
        

      case 'ai.off_topic_warning':
        setOffTopicCount(data.count || 0);
        const warningMessage = {
          id: Date.now(),
          sender: 'ai',
          text: data.message,
          timestamp: new Date(),
          isWarning: true,
          hasAudio: true
        };
        setMessages(prev => [...prev, warningMessage]);
        speakText(data.message, 1);
        break;

      case 'ai.hint_text':
        setHintsUsed(prev => prev + 1);
        // Store the hint text from backend
        setCurrentHint(data.text);
        // Auto-expand help section when hint arrives
        setHelpExpanded(true);
        // Scroll to show the hint
        setTimeout(() => {
          const messagesContainer = document.querySelector('.messages-container');
          if (messagesContainer) {
            messagesContainer.scrollTo({
              top: messagesContainer.scrollHeight,
              behavior: 'smooth'
            });
          }
        }, 200);
        console.log('💡 Hint received from backend:', data.text);
        break;

      case 'ai.question_clarified':
        // Show translation
        const translationMessage = {
          id: Date.now(),
          sender: 'ai',
          text: `${data.original} → ${data.translation}`,
          timestamp: new Date(),
          isTranslation: true
        };
        setMessages(prev => [...prev, translationMessage]);
        break;

      case 'ai.translated':
        // Handle explicit translation response
        console.log('🌐 Translation received:', data);
        
        // If we have a pending translation for this text, update it
        // The backend sends 'original' and 'translation'
        if (window.pendingTranslations && data.original) {
          const messageId = window.pendingTranslations.get(data.original);
          
          if (messageId) {
            // Store translation
            setTranslatedMessages(prev => ({
              ...prev,
              [messageId]: data.translation
            }));
            
            // Remove from translating set
            setTranslatingMessages(prev => {
              const newSet = new Set(prev);
              newSet.delete(messageId);
              return newSet;
            });
            
            // Clean up pending translation
            window.pendingTranslations.delete(data.original);
          }
        } else {
            // Fallback: if we can't match it to a specific message, show it as a new system message
            // or just log it if it was a general translation request
             const generalTranslationMessage = {
              id: Date.now(),
              sender: 'ai',
              text: `${data.original} → ${data.translation}`,
              timestamp: new Date(),
              isTranslation: true
            };
            setMessages(prev => [...prev, generalTranslationMessage]);
        }
        break;

      case 'ai.translation':
        // Handle translation response
        console.log('🌐 Translation received:', data);
        
        // Find the message ID that requested this translation
        if (window.pendingTranslations && data.original_text) {
          const messageId = window.pendingTranslations.get(data.original_text);
          
          if (messageId) {
            // Store translation
            setTranslatedMessages(prev => ({
              ...prev,
              [messageId]: data.translated_text
            }));
            
            // Remove from translating set
            setTranslatingMessages(prev => {
              const newSet = new Set(prev);
              newSet.delete(messageId);
              return newSet;
            });
            
            // Clean up pending translation
            window.pendingTranslations.delete(data.original_text);
          }
        }
        break;

      case 'status.waiting_for_student':
        // Clear processing timeout since we got a response
        if (processingTimeoutRef.current) {
          clearTimeout(processingTimeoutRef.current);
          processingTimeoutRef.current = null;
        }
        
        setCurrentStep(data.step || currentStep);
        setIsSendingMessage(false);
        setShowUserHelp(true);
        setIsRecording(false); // Make sure recording is stopped
        isRecordingStoppedRef.current = false; // Reset for next recording
        // Clear hint when ready for new input
        setCurrentHint(null);
        setHelpExpanded(false);
        // Button should be enabled now - WebSocket is ready
        console.log('✅ Ready for student input at step', data.step);
        
        // If we didn't just receive a correction, it means the user's answer was correct
        // Reset error counter
        if (!justReceivedCorrectionRef.current) {
          console.log('✅ User answer was correct - resetting error counter');
          setConsecutiveErrors(0);
        } else {
          console.log('⚠️ User answer had error - keeping error counter');
        }
        // Reset the flag for next turn
        justReceivedCorrectionRef.current = false;
        
        // Note: Silence detection is handled by the auto-stop recording feature (1.5s silence)
        // We don't need a separate 30s timeout here
        break;

      case 'session.ended':
        console.log('🔚 Session ended:', data.reason);
        handleSessionEnd(data);
        break;

      case 'session.completed':
        console.log('✅ Session completed!');
        handleSessionComplete(data);
        break;

      case 'error':
        console.error('❌ WebSocket error:', data);
        const errorMessage = data.message || data.code || 'An error occurred';
        
        // Handle specific error codes
        if (data.code === 'NO_SCRIPT') {
          setErrorContent({
            title: 'Script Not Found',
            message: 'Dialog script not found for this lesson. This lesson needs a dialog script to be created in the backend. Please contact support or ensure the script is configured for this lesson.',
            code: 'NO_SCRIPT'
          });
        } else if (data.code === 'DEMO_LIMIT_REACHED') {
          setErrorContent({
            title: 'Demo Limit Reached',
            message: errorMessage,
            code: 'DEMO_LIMIT_REACHED'
          });
        } else {
          setErrorContent({
            title: 'Connection Error',
            message: errorMessage,
            code: data.code
          });
        }
        
        setShowReadyScreen(true);
        setWsStatus('error');
        setIsInitializing(false);
        sessionStartedRef.current = false;
        break;

      default:
        console.log('Unknown message type:', data.type);
    }
  };

  // Handle audio chunk playback
  const handleAudioChunk = (data) => {
    try {
      // Decode base64 audio
      const binaryString = atob(data.data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }

      // Create blob
      const mimeType = data.format === 'mp3' ? 'audio/mpeg' : `audio/${data.format}`;
      const blob = new Blob([bytes], { type: mimeType });

      // Store chunks for concatenation
      if (!audioChunksRef.current) {
        audioChunksRef.current = [];
      }
      audioChunksRef.current.push(blob);

      // If this is the last chunk, play all chunks together
      if (data.is_last) {
        // Combine all chunks into one blob
        const combinedBlob = new Blob(audioChunksRef.current, { type: mimeType });
        const audioUrl = URL.createObjectURL(combinedBlob);
        
        // Store audio URL for the most recent AI message (for replay functionality)
        setMessages(prev => {
          const lastAiMessageIndex = prev.length - 1;
          if (lastAiMessageIndex >= 0 && prev[lastAiMessageIndex].sender === 'ai') {
            const messageId = prev[lastAiMessageIndex].id;
            messageAudioUrlsRef.current[messageId] = audioUrl;
            console.log('💾 Stored audio URL for message:', messageId);
          }
          return prev;
        });
        
        // Stop any currently playing audio
        if (currentAudioRef.current) {
          currentAudioRef.current.pause();
          currentAudioRef.current = null;
        }

        // Play the combined audio
        const audio = new Audio(audioUrl);
        currentAudioRef.current = audio;
        
        audio.onended = () => {
          // Don't revoke URL here - keep it for replay functionality
          currentAudioRef.current = null;
        };

        audio.onerror = (err) => {
          console.error('Error playing audio:', err);
          currentAudioRef.current = null;
        };

        audio.play().catch(err => {
          console.error('Error playing audio:', err);
          currentAudioRef.current = null;
        });

        // Clear chunks for next audio
        audioChunksRef.current = [];
      }
    } catch (error) {
      console.error('Error handling audio chunk:', error);
      audioChunksRef.current = [];
    }
  };

  // Handle session end
  const handleSessionEnd = (data) => {
    setIsSendingMessage(false);
    setIsRecording(false);
    if (data.reason === 'off_topic' && data.off_topic_count >= 3) {
      alert('Session ended: Too many off-topic responses');
    }
    // Show summary or navigate
  };

  // Handle session complete
  const handleSessionComplete = (data) => {
    setIsLessonCompleted(true);
    setshowCompletion(true);
    if (data.feedback) {
      console.log('Session feedback:', data.feedback);
    }
    
    // Check completion status after dialogue finishes
    // Backend now sends session.completed immediately, so we can check sooner
    // But still wait a bit to ensure backend has processed everything
    const score = data.score || 100; // Use backend score or default to 100%
    if (onActivityComplete) {
      setTimeout(() => {
        onActivityComplete(score);
      }, 2000); // Increased to 2 seconds for safety
    }
  };





  // Start lesson - connect WebSocket and start session
  const startLesson = async () => {
    if (!lesson?.id) {
      console.error('No lesson ID available');
      alert('Unable to start lesson: No lesson data available.');
      return;
    }

    // Prevent multiple clicks
    if (isInitializing || wsStatus === 'connecting' || wsStatus === 'connected') {
      console.log('Already connecting or connected, ignoring click');
      return;
    }

    setIsInitializing(true);
    setWsStatus('connecting');
    sessionStartedRef.current = false; // Reset session started flag

    try {
      // Track first lesson opened when user starts dialogue
      trackLessonOpened(lesson.id);

      // Optional: Check if script exists before connecting
      // This is optional - the backend will also check and return an error if missing
      // Uncomment if you want to check before connecting:
      /*
      const scriptExists = await checkDialogScriptExists(lesson.id);
      if (scriptExists === false) {
        alert(
          'Dialog script not found for this lesson.\n\n' +
          'This lesson needs a dialog script to be created in the backend.\n' +
          'Please contact support or ensure the script is configured for this lesson.'
        );
        setIsInitializing(false);
        setWsStatus('disconnected');
        return;
      }
      */

      // Create WebSocket manager
      const wsManager = new WebSocketManager(
        lesson.id,
        handleWebSocketMessage,
        (error) => {
          console.error('WebSocket error:', error);
          setWsStatus('error');
          setIsInitializing(false);
          sessionStartedRef.current = false;
          alert('Connection error. Please try again.');
        },
        (event) => {
          console.log('WebSocket closed:', event.code, event.reason);
          setWsStatus('disconnected');
          sessionStartedRef.current = false;
        }
      );

      wsManagerRef.current = wsManager;

      // Connect to WebSocket
      await wsManager.connect();
      // Don't set status to 'connected' here - wait for status.update message
      // Don't start session here - wait for status.update with 'connected' status
      
      // The session will be started automatically when we receive status.update with 'connected'
      // Keep initializing state until session.started is received
    } catch (error) {
      console.error('Error starting lesson:', error);
      setWsStatus('error');
      setIsInitializing(false);
      sessionStartedRef.current = false;
      alert('Unable to connect to the server. Please try again.');
    }
  };

  // Add message to chat (for display only - WebSocket handles actual messages)
  const addMessage = (sender, text) => {
    const newMessage = { sender, text, id: Date.now(), timestamp: new Date() };
    setMessages(prev => [...prev, newMessage]);

    if (sender === 'user') {
      setTurnCount(prev => prev + 1);
      setShowUserHelp(false);

      // Check for vocabulary usage
      selectedTopic.vocabulary.forEach(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        if (regex.test(text)) {
          markVocabularyUsed(word);
        }
      });
    } else if (sender === 'ai') {
      setLastAIQuestion(text);
      setShowUserHelp(true);
      speakText(text);
    }
  };

  // Mark vocabulary as used
  const markVocabularyUsed = async (word) => {
    setUsedVocabulary(prev => {
      if (!prev.has(word)) {
        const newSet = new Set(prev);
        newSet.add(word);

        // Check if all words are used
        if (newSet.size === selectedTopic.vocabulary.length && !isLessonCompleted) {
          setIsLessonCompleted(true);
          
          // Track AI dialogue completion
          const lessonId = lesson?.id || currentLessonId;
          if (lessonId) {
            trackAIDialogueCompleted(lessonId, turnCount + 1); // +1 because this turn is being added
            
            // Check if lesson is completed after dialogue
            (async () => {
              const { getVideoLessonProgress } = await import('../../../lib/videoLessonApi');
              const progress = await getVideoLessonProgress(lessonId);
              if (progress?.completion_status === 'COMPLETED') {
                trackLessonCompleted(lessonId);
              }
            })();
          }
          
          setTimeout(() => setshowCompletion(true), 3500);
        }

        return newSet;
      }
      return prev;
    });
  };


  // Text to speech
  const speakText = (text, rate = 0.9) => {
    if ('speechSynthesis' in window) {
      try {
        speechSynthesis.cancel();
      } catch (_) {}
      const utterance = new SpeechSynthesisUtterance(text);
      try { utterance.lang = 'en-US'; } catch (_) {}
      utterance.rate = rate;
      utterance.pitch = 1.1;
      speechSynthesis.speak(utterance);
    }
  };

  // Play hint example text (strips HTML, uses en-US TTS)
  const playHintText = (htmlOrText, rate = 1) => {
    const plain = (typeof htmlOrText === 'string')
      ? htmlOrText.replace(/<[^>]*>/g, '').trim()
      : '';
    if (plain) speakText(plain, rate);
  };

  // Voice recording functions
  const mediaRecorderRef = useRef(null);
  const silenceTimeoutRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const isRecordingStoppedRef = useRef(false); // Flag to prevent sending chunks after stop
  const processingTimeoutRef = useRef(null); // Timeout to reset processing state

  // Start recording - phone-like behavior (press once, auto-stop on silence)
  const handleStartRecording = () => {
    if (!isRecording && !isSendingMessage) {
      startRecording();
    }
    // If already recording, user can still manually stop by pressing again
    // But we prefer auto-stop on silence for natural conversation
  };

  // Manual stop (optional - mainly for emergency stop)
  const handleStopRecording = () => {
    if (isRecording) {
      stopRecording();
    }
  };

  const startRecording = async () => {
    // Check WebSocket connection
    if (!wsManagerRef.current || !wsManagerRef.current.isReady()) {
      alert('Not connected. Please start the lesson first.');
      return;
    }

    // Prevent starting if already recording
    if (isRecording) {
      console.log('Already recording, ignoring start request');
      return;
    }

    // Clear silence detection timeout when user starts recording
    if (silenceDetectionTimeoutRef.current) {
      clearTimeout(silenceDetectionTimeoutRef.current);
      silenceDetectionTimeoutRef.current = null;
      console.log('🎤 User started recording - cleared silence timeout');
    }

    // Reset stopped flag and clear previous recording
    isRecordingStoppedRef.current = false;
    recordedChunksRef.current = [];
    setRecordedAudioBlob(null);
    setRecordingDuration(0);

    try {
      console.log('🎤 Requesting microphone access...');
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      console.log('✅ Microphone access granted');

      // Try to use a compatible audio format
      let options = {};
      if (MediaRecorder.isTypeSupported('audio/webm;codecs=opus')) {
        options.mimeType = 'audio/webm;codecs=opus';
      } else if (MediaRecorder.isTypeSupported('audio/webm')) {
        options.mimeType = 'audio/webm';
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        options.mimeType = 'audio/mp4';
      }

      mediaRecorderRef.current = new MediaRecorder(stream, options);
      chunkIndexRef.current = 0;

      console.log('📹 MediaRecorder initialized with mimeType:', mediaRecorderRef.current.mimeType);

      // Start recording timer
      const startTime = Date.now();
      setRecordingStartTime(startTime);
      recordingTimerRef.current = setInterval(() => {
        const elapsed = Math.floor((Date.now() - startTime) / 1000);
        setRecordingDuration(elapsed);
      }, 100);

      // Set up audio analysis for silence detection
      setupSilenceDetection(stream);

      // Store audio chunks locally AND send to backend
      mediaRecorderRef.current.ondataavailable = async (event) => {
        // Check if recording has been stopped - if so, ignore all chunks
        if (isRecordingStoppedRef.current) {
          console.log('⚠️ Ignoring audio chunk - recording already stopped');
          return;
        }

        // Store chunk locally for playback
        if (event.data && event.data.size > 0) {
          recordedChunksRef.current.push(event.data);
        }

        // Send chunks to backend
        if (event.data && event.data.size > 0 && wsManagerRef.current && wsManagerRef.current.isReady()) {
          try {
            console.log(`📤 Sending audio chunk ${chunkIndexRef.current}, size: ${event.data.size} bytes`);
            const base64Audio = await blobToBase64(event.data);
            const format = mediaRecorderRef.current.mimeType.includes('webm') ? 'webm' : 
                          mediaRecorderRef.current.mimeType.includes('mp4') ? 'mp4' : 'webm';
            
            wsManagerRef.current.sendAudioChunk(
              base64Audio,
              format,
              chunkIndexRef.current,
              1,
              false
            );
            chunkIndexRef.current++;
          } catch (error) {
            console.error('❌ Error sending audio chunk:', error);
          }
        }
      };

      mediaRecorderRef.current.onerror = (event) => {
        console.error('❌ MediaRecorder error:', event.error);
        setIsRecording(false);
        cleanupRecording();
      };

      mediaRecorderRef.current.onstop = async () => {
        console.log('🛑 MediaRecorder stopped');
        
        // Stop recording timer
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
        // Mark as stopped to prevent any further chunks from being sent
        isRecordingStoppedRef.current = true;
        
        // Create blob from recorded chunks for local playback
        if (recordedChunksRef.current.length > 0) {
          const mimeType = mediaRecorderRef.current.mimeType;
          const audioBlob = new Blob(recordedChunksRef.current, { type: mimeType });
          setRecordedAudioBlob(audioBlob);
          console.log('💾 Stored recorded audio blob for preview');
          
          // Generate waveform data from audio
          generateWaveformData(audioBlob);
        }
        
        // DON'T send stop_recording command yet - wait for user to click send
        // DON'T set isSendingMessage - we're waiting for user action
        
        setIsRecording(false);
        chunkIndexRef.current = 0;

        // Clean up stream but keep the recording
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
          streamRef.current = null;
        }

        // Clean up audio context
        if (audioContextRef.current) {
          audioContextRef.current.close();
          audioContextRef.current = null;
        }

        // Clear silence detection
        if (silenceTimeoutRef.current) {
          if (typeof silenceTimeoutRef.current === 'object' && silenceTimeoutRef.current.isActive !== undefined) {
            silenceTimeoutRef.current.isActive = false;
          } else if (typeof silenceTimeoutRef.current === 'number') {
            clearTimeout(silenceTimeoutRef.current);
          }
          silenceTimeoutRef.current = null;
        }
      };

      // Start recording with timeslice for chunked streaming
      console.log('🎙️ Starting MediaRecorder...');
      mediaRecorderRef.current.start(100); // Send chunks every 100ms
      setIsRecording(true);
      console.log('✅ Recording started successfully');
    } catch (error) {
      console.error('❌ Error starting recording:', error);
      setIsRecording(false);
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        alert('Microphone access denied. Please allow microphone access to use voice recording.');
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        alert('No microphone found. Please connect a microphone and try again.');
      } else {
        alert(`Error accessing microphone: ${error.message}`);
      }
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current) {
      if (mediaRecorderRef.current.state === 'recording') {
        console.log('🛑 Stopping MediaRecorder...');
        // Don't request final data - just stop cleanly
        // This prevents extra chunks from being generated after stop command
        mediaRecorderRef.current.stop();
      } else if (mediaRecorderRef.current.state === 'inactive') {
        console.log('⚠️ MediaRecorder already stopped');
        setIsRecording(false);
        cleanupRecording();
      } else {
        console.log('⚠️ MediaRecorder not in recording state:', mediaRecorderRef.current.state);
        setIsRecording(false);
        cleanupRecording();
      }
    } else {
      console.warn('⚠️ No MediaRecorder instance to stop');
      setIsRecording(false);
    }
  };

  // Convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result.split(',')[1]; // Remove data:audio/...;base64, prefix
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  // Set up silence detection for phone-like auto-stop
  const setupSilenceDetection = (stream) => {
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      analyserRef.current = audioContextRef.current.createAnalyser();
      const source = audioContextRef.current.createMediaStreamSource(stream);
      source.connect(analyserRef.current);

      analyserRef.current.fftSize = 256;
      analyserRef.current.smoothingTimeConstant = 0.8;
      const bufferLength = analyserRef.current.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);

      // Use refs to track state so closure can access current values
      const silenceDetectionRef = {
        silenceStart: null,
        hasDetectedSound: false,
        recordingStartTime: Date.now(),
        isActive: true
      };

      const SILENCE_THRESHOLD = 20; // Adjust this value to change sensitivity (lower = more sensitive)
      const SILENCE_DURATION = 1500; // 1.5 seconds of silence before auto-stop (phone-like)
      const MIN_RECORDING_DURATION = 500; // Minimum 500ms recording to avoid accidental stops

      const checkAudioLevel = () => {
        // Check if we should continue (using refs instead of state)
        if (isRecordingStoppedRef.current || !silenceDetectionRef.isActive) {
          console.log('🛑 Silence detection stopped');
          return;
        }

        if (!analyserRef.current) {
          return;
        }

        analyserRef.current.getByteFrequencyData(dataArray);

        // Calculate average volume
        let sum = 0;
        for (let i = 0; i < bufferLength; i++) {
          sum += dataArray[i];
        }
        const average = sum / bufferLength;

        // Log volume for debugging (can be removed later)
        if (chunkIndexRef.current % 50 === 0) { // Log every 50 chunks (~5 seconds)
          console.log(`🔊 Audio level: ${average.toFixed(1)} (threshold: ${SILENCE_THRESHOLD})`);
        }

        if (average >= SILENCE_THRESHOLD) {
          // Sound detected
          if (!silenceDetectionRef.hasDetectedSound) {
            console.log('🎤 Sound detected - user started speaking');
          }
          silenceDetectionRef.hasDetectedSound = true;
          silenceDetectionRef.silenceStart = null; // Reset silence timer
        } else {
          // Silence detected
          // Only start counting silence after user has spoken
          if (silenceDetectionRef.hasDetectedSound) {
            if (silenceDetectionRef.silenceStart === null) {
              silenceDetectionRef.silenceStart = Date.now();
              console.log('🔇 Silence detected - starting timer');
            } else {
              const silenceDuration = Date.now() - silenceDetectionRef.silenceStart;
              const recordingDuration = Date.now() - silenceDetectionRef.recordingStartTime;
              
              // Log progress every 500ms
              if (silenceDuration % 500 < 50) {
                console.log(`⏱️ Silence duration: ${(silenceDuration / 1000).toFixed(1)}s / ${(SILENCE_DURATION / 1000).toFixed(1)}s`);
              }
              
              // Auto-stop if silence detected for SILENCE_DURATION and minimum recording time has passed
              if (silenceDuration >= SILENCE_DURATION && recordingDuration >= MIN_RECORDING_DURATION) {
                console.log('🔇 Auto-stopping recording due to silence (phone-like behavior)');
                silenceDetectionRef.isActive = false;
                // Mark as stopped BEFORE calling stopRecording to prevent race condition
                isRecordingStoppedRef.current = true;
                
                // Increment consecutive silences counter
                setConsecutiveSilences(prev => {
                  const newCount = prev + 1;
                  console.log('😶 Consecutive silences (auto-stop):', newCount, '/', STRUGGLE_THRESHOLD);
                  
                  // Show struggle modal if threshold reached
                  if (newCount >= STRUGGLE_THRESHOLD) {
                    const now = Date.now();
                    if (now - lastErrorCheckTimeRef.current > 5000) {
                      console.log('🚨 Showing struggle modal for silence');
                      lastErrorCheckTimeRef.current = now;
                      setStruggleVariant('silence');
                      setShowStruggleModal(true);
                    }
                  }
                  
                  return newCount;
                });
                
                stopRecording();
                return;
              }
            }
          }
        }

        // Continue checking
        if (!isRecordingStoppedRef.current && silenceDetectionRef.isActive) {
          requestAnimationFrame(checkAudioLevel);
        }
      };

      // Start checking audio levels immediately
      console.log('🎧 Starting silence detection...');
      checkAudioLevel();

      // Store ref for cleanup
      silenceTimeoutRef.current = silenceDetectionRef;
    } catch (error) {
      console.warn('Could not set up silence detection:', error);
      // Fallback: auto-stop after 30 seconds
      silenceTimeoutRef.current = setTimeout(() => {
        if (isRecording && !isRecordingStoppedRef.current) {
          console.log('Auto-stopping recording after 30 seconds (fallback)');
          stopRecording();
        }
      }, 30000);
    }
  };

  // Clean up recording resources
  const cleanupRecording = () => {
    // Stop all tracks to release microphone
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }

    // Clean up audio context
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    // Clear timeout or silence detection ref
    if (silenceTimeoutRef.current) {
      // Check if it's a timeout or silence detection ref
      if (typeof silenceTimeoutRef.current === 'object' && silenceTimeoutRef.current.isActive !== undefined) {
        // It's a silence detection ref
        silenceTimeoutRef.current.isActive = false;
      } else if (typeof silenceTimeoutRef.current === 'number') {
        // It's a timeout ID
        clearTimeout(silenceTimeoutRef.current);
      }
      silenceTimeoutRef.current = null;
    }

    // Reset stopped flag
    isRecordingStoppedRef.current = false;
  };


  // Get contextual example - use backend hint if available, otherwise use frontend examples
  const getContextualExample = () => {
    // If we have a hint from backend, use it
    if (currentHint) {
      return currentHint;
    }

    // Otherwise, use frontend fallback examples
    const unusedWords = selectedTopic.vocabulary.filter(word => !usedVocabulary.has(word));

    if (unusedWords.length > 0) {
      const examples = exampleAnswers[selectedTopic.title];
      const relevantExamples = examples.filter(example =>
        unusedWords.some(word => example.toLowerCase().includes(word))
      );

      const selectedExample = relevantExamples.length > 0
        ? relevantExamples[Math.floor(Math.random() * relevantExamples.length)]
        : examples[Math.floor(Math.random() * examples.length)];

      let highlightedExample = selectedExample;
      selectedTopic.vocabulary.forEach(word => {
        if (unusedWords.includes(word)) {
          const regex = new RegExp(`\\b${word}\\b`, 'gi');
          highlightedExample = highlightedExample.replace(regex, `<strong style="color: var(--purple-main); background: linear-gradient(to bottom, transparent 60%, rgba(124, 58, 237, 0.2) 60%); padding: 0 2px;">${word}</strong>`);
        }
      });

      return highlightedExample;
    } else {
      return "Great job! You've used all the vocabulary words. Feel free to continue the conversation naturally!";
    }
  };

  

  // Replay audio for a specific message
  const replayAudio = (messageId) => {
    const audioUrl = messageAudioUrlsRef.current[messageId];
    
    if (!audioUrl) {
      const msg = messages.find(m => m.id === messageId);
      if (msg?.text) {
        speakText(msg.text, 1);
      } else {
        console.warn('⚠️ No audio URL or text found for message:', messageId);
      }
      return;
    }

    console.log('🔊 Replaying audio for message:', messageId);
    
    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Play the audio
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;
    
    audio.onended = () => {
      currentAudioRef.current = null;
    };

    audio.onerror = (err) => {
      console.error('Error replaying audio:', err);
      currentAudioRef.current = null;
    };

    audio.play().catch(err => {
      console.error('Error replaying audio:', err);
      currentAudioRef.current = null;
    });
  };

  // Replay audio slowly for a specific message (0.5–0.7x speed)
  const replayAudioSlowly = (messageId, rate = 0.5) => {
    const audioUrl = messageAudioUrlsRef.current[messageId];
    
    if (!audioUrl) {
      const msg = messages.find(m => m.id === messageId);
      if (msg?.text) {
        const clamped = Math.max(0.5, Math.min(rate, 0.7));
        speakText(msg.text, clamped);
      } else {
        console.warn('⚠️ No audio URL or text found for message:', messageId);
      }
      return;
    }

    console.log('🐌 Replaying audio slowly for message:', messageId, 'rate:', rate);
    
    // Stop any currently playing audio
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Clamp rate to 0.5–0.7 as requested
    const clampedRate = Math.max(0.5, Math.min(rate, 0.7));

    // Play the audio at a slower rate
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;
    try {
      // Keep pitch natural where supported
      if ('preservesPitch' in audio) audio.preservesPitch = true;
      if ('mozPreservesPitch' in audio) audio.mozPreservesPitch = true;
      if ('webkitPreservesPitch' in audio) audio.webkitPreservesPitch = true;
    } catch (_) {}
    // Apply rate in multiple lifecycle points to ensure browsers honor it
    audio.defaultPlaybackRate = clampedRate;
    audio.playbackRate = clampedRate;
    audio.addEventListener('loadedmetadata', () => {
      audio.defaultPlaybackRate = clampedRate;
      audio.playbackRate = clampedRate;
    });
    audio.addEventListener('playing', () => {
      audio.playbackRate = clampedRate;
    });
    
    audio.onended = () => {
      currentAudioRef.current = null;
    };

    audio.onerror = (err) => {
      console.error('Error replaying audio slowly:', err);
      currentAudioRef.current = null;
    };

    audio.play()
      .then(() => {
        audio.playbackRate = clampedRate;
      })
      .catch(err => {
        console.error('Error replaying audio slowly:', err);
        currentAudioRef.current = null;
      });
  };

  const playTranslation = async (messageId, rate = 1) => {
    const text = translatedMessages[messageId];
    if (!text) return;
    try {
      const r = Math.max(0.5, Math.min(rate, 1.5));
      const httpTtsTemplate = (typeof window !== 'undefined' && window.localStorage) ? window.localStorage.getItem('UK_TTS_HTTP_URL') : null;
      if (httpTtsTemplate) {
        try {
          const lang = 'uk-UA';
          const url = httpTtsTemplate
            .replace('{text}', encodeURIComponent(text))
            .replace('{lang}', encodeURIComponent(lang))
            .replace('{rate}', encodeURIComponent(String(r)));
          const resp = await fetch(url);
          const ct = resp.headers.get('content-type') || '';
          if (resp.ok && (ct.includes('audio/') || ct.includes('application/octet-stream'))) {
            const buf = await resp.arrayBuffer();
            const blob = new Blob([buf], { type: ct.includes('audio/') ? ct : 'audio/mpeg' });
            const audioUrl = URL.createObjectURL(blob);
            const audio = new Audio(audioUrl);
            audio.playbackRate = r;
            audio.onended = () => URL.revokeObjectURL(audioUrl);
            await audio.play().catch(() => {});
            return;
          }
        } catch (_) {}
      }
      if ('speechSynthesis' in window) {
        const voice = await getUkrainianVoice();
        try { speechSynthesis.cancel(); } catch (_) {}
        const u = new SpeechSynthesisUtterance(text);
        u.lang = 'uk-UA';
        if (voice) {
          u.voice = voice;
        } else {
          console.warn('Ukrainian voice not detected; speaking with uk-UA locale without explicit voice.');
        }
        u.rate = r;
        u.pitch = 1;
        speechSynthesis.speak(u);
      }
    } catch (e) {
      console.error('TTS translation error:', e);
    }
  };

  // Send recorded audio message
  const sendRecordedAudio = async () => {
    if (!recordedAudioBlob) {
      console.warn('⚠️ No recorded audio to send');
      return;
    }

    console.log('📤 Sending recorded audio message...');
    setIsSendingMessage(true);

    try {
      // Wait a short delay to ensure all buffered chunks are sent
      await new Promise(resolve => setTimeout(resolve, 150));
      
      // Send stop recording command to backend
      if (wsManagerRef.current && wsManagerRef.current.isReady()) {
        console.log('📤 Sending stop_recording command');
        wsManagerRef.current.stopRecording();
      }

      // Clear the recorded audio
      setRecordedAudioBlob(null);
      setRecordingDuration(0);
      recordedChunksRef.current = [];

      // Set timeout to reset processing state if backend doesn't respond
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
      processingTimeoutRef.current = setTimeout(() => {
        console.warn('⚠️ Processing timeout - resetting state');
        setIsSendingMessage(false);
        processingTimeoutRef.current = null;
      }, 30000); // 30 second timeout
    } catch (error) {
      console.error('❌ Error sending recorded audio:', error);
      setIsSendingMessage(false);
    }
  };

  // Delete recorded audio
  const deleteRecordedAudio = () => {
    console.log('🗑️ Deleting recorded audio');
    
    // Stop playback if playing
    if (recordedAudioRef.current) {
      recordedAudioRef.current.pause();
      recordedAudioRef.current = null;
    }
    
    // Clear playback interval
    if (playbackIntervalRef.current) {
      clearInterval(playbackIntervalRef.current);
      playbackIntervalRef.current = null;
    }
    
    // Cancel animation frame
    if (playbackAnimationFrameRef.current) {
      cancelAnimationFrame(playbackAnimationFrameRef.current);
      playbackAnimationFrameRef.current = null;
    }
    
    setRecordedAudioBlob(null);
    setRecordingDuration(0);
    setIsPlayingRecording(false);
    setAudioWaveformData([]);
    setPlaybackProgress(0);
    setCurrentPlaybackTime(0);
    setAudioDuration(0);
    recordedChunksRef.current = [];
    
    // Reset stopped flag
    isRecordingStoppedRef.current = false;
  };

  // Generate waveform data from audio blob
  const generateWaveformData = async (audioBlob) => {
    try {
      const arrayBuffer = await audioBlob.arrayBuffer();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);
      
      // Store audio duration
      setAudioDuration(audioBuffer.duration);
      
      // Get audio data from first channel
      const rawData = audioBuffer.getChannelData(0);
      const samples = 50; // Number of bars to display
      const blockSize = Math.floor(rawData.length / samples);
      const filteredData = [];
      
      // Calculate amplitude for each block
      for (let i = 0; i < samples; i++) {
        let blockStart = blockSize * i;
        let sum = 0;
        for (let j = 0; j < blockSize; j++) {
          sum += Math.abs(rawData[blockStart + j]);
        }
        // Normalize to 0-1 range
        filteredData.push(sum / blockSize);
      }
      
      // Normalize the entire array to make use of full height range
      const max = Math.max(...filteredData);
      const normalizedData = filteredData.map(n => n / max);
      
      setAudioWaveformData(normalizedData);
      console.log('📊 Generated waveform data:', normalizedData.length, 'bars, duration:', audioBuffer.duration.toFixed(2), 's');
      
      // Close audio context
      audioContext.close();
    } catch (error) {
      console.error('❌ Error generating waveform:', error);
      // Fallback to default waveform
      setAudioWaveformData(Array(50).fill(0.5));
      setAudioDuration(recordingDuration);
    }
  };

  // Function to update progress using requestAnimationFrame for reliable tracking
  const startProgressTracking = () => {
    // Cancel any existing animation frame
    if (playbackAnimationFrameRef.current) {
      cancelAnimationFrame(playbackAnimationFrameRef.current);
      playbackAnimationFrameRef.current = null;
    }

    const updateProgress = () => {
      if (!recordedAudioRef.current) {
        playbackAnimationFrameRef.current = null;
        return;
      }

      const audio = recordedAudioRef.current;
      
      // Check if still playing
      if (audio.paused || audio.ended) {
        if (audio.ended) {
          setIsPlayingRecording(false);
          setPlaybackProgress(1);
          // Get final duration from audio element
          const finalDuration = audio.duration > 0 ? audio.duration : audioDuration;
          setCurrentPlaybackTime(finalDuration || 0);
        }
        playbackAnimationFrameRef.current = null;
        return;
      }

      const currentTime = audio.currentTime || 0;
      // Always use audio.duration as primary source (most reliable), fallback to state
      let duration = audio.duration;
      
      // If audio.duration is not available yet, use state value
      if (!duration || !isFinite(duration) || duration <= 0) {
        duration = audioDuration;
      }
      
      // Update state duration if we got a valid value from audio element
      if (audio.duration > 0 && isFinite(audio.duration) && (!audioDuration || audioDuration === 0)) {
        setAudioDuration(audio.duration);
        duration = audio.duration; // Use the fresh value
      }
      
      if (duration > 0 && isFinite(duration) && !isNaN(currentTime) && currentTime >= 0) {
        const progress = Math.min(Math.max(currentTime / duration, 0), 1);
        setPlaybackProgress(progress);
        setCurrentPlaybackTime(currentTime);
      }

      // Continue tracking
      playbackAnimationFrameRef.current = requestAnimationFrame(updateProgress);
    };

    // Start tracking immediately
    playbackAnimationFrameRef.current = requestAnimationFrame(updateProgress);
  };

  // Play recorded audio - simplified version for voice message preview
  const playRecordedAudio = () => {
    if (!recordedAudioBlob) {
      console.warn('⚠️ No recorded audio to play');
      return;
    }

    try {
      // If already playing, pause it
      if (isPlayingRecording && recordedAudioRef.current) {
        recordedAudioRef.current.pause();
        setIsPlayingRecording(false);
        // Cancel animation frame
        if (playbackAnimationFrameRef.current) {
          cancelAnimationFrame(playbackAnimationFrameRef.current);
          playbackAnimationFrameRef.current = null;
        }
        return;
      }

      // If paused (audio exists but not playing), resume playback
      if (recordedAudioRef.current && !isPlayingRecording) {
        setIsPlayingRecording(true);
        recordedAudioRef.current.play().then(() => {
          // Restart progress tracking
          startProgressTracking();
        }).catch(err => {
          console.error('❌ Error resuming playback:', err);
          setIsPlayingRecording(false);
        });
        return;
      }

      // First time playing - create new audio element
      const audioUrl = URL.createObjectURL(recordedAudioBlob);
      const audio = new Audio(audioUrl);
      recordedAudioRef.current = audio;

      // Set up event handlers
      audio.onloadedmetadata = () => {
        const duration = audio.duration;
        if (isFinite(duration) && duration > 0) {
          setAudioDuration(duration);
          console.log('📊 Audio duration loaded:', duration.toFixed(2), 's');
        }
      };

      audio.onended = () => {
        setIsPlayingRecording(false);
        setPlaybackProgress(1); // Set to 1 (100%) when ended
        setCurrentPlaybackTime(audioDuration || 0);
        
        // Cancel animation frame
        if (playbackAnimationFrameRef.current) {
          cancelAnimationFrame(playbackAnimationFrameRef.current);
          playbackAnimationFrameRef.current = null;
        }
        
        URL.revokeObjectURL(audioUrl);
        recordedAudioRef.current = null;
      };

      audio.onerror = (err) => {
        console.error('❌ Error playing audio:', err);
        setIsPlayingRecording(false);
        setPlaybackProgress(0);
        setCurrentPlaybackTime(0);
        
        // Cancel animation frame
        if (playbackAnimationFrameRef.current) {
          cancelAnimationFrame(playbackAnimationFrameRef.current);
          playbackAnimationFrameRef.current = null;
        }
        
        URL.revokeObjectURL(audioUrl);
        recordedAudioRef.current = null;
      };

      // Start playback
      audio.play().then(() => {
        setIsPlayingRecording(true);
        // Reset progress to 0 at start
        setPlaybackProgress(0);
        setCurrentPlaybackTime(0);
        
        // Ensure duration is set immediately if available
        if (audio.duration > 0 && isFinite(audio.duration) && (!audioDuration || audioDuration === 0)) {
          setAudioDuration(audio.duration);
        }
        
        // Start progress tracking immediately
        startProgressTracking();
      }).catch(err => {
        console.error('❌ Error starting playback:', err);
        setIsPlayingRecording(false);
        
        // Cancel animation frame
        if (playbackAnimationFrameRef.current) {
          cancelAnimationFrame(playbackAnimationFrameRef.current);
          playbackAnimationFrameRef.current = null;
        }
        
        URL.revokeObjectURL(audioUrl);
        recordedAudioRef.current = null;
      });
    } catch (error) {
      console.error('❌ Error in playRecordedAudio:', error);
      setIsPlayingRecording(false);
      setPlaybackProgress(0);
      setCurrentPlaybackTime(0);
      
      // Cancel animation frame
      if (playbackAnimationFrameRef.current) {
        cancelAnimationFrame(playbackAnimationFrameRef.current);
        playbackAnimationFrameRef.current = null;
      }
    }
  };

  // Translate message to user's selected language
  const translateMessage = async (messageId, messageText) => {
    // Check if already translated
    if (translatedMessages[messageId]) {
      // Toggle back to original
      setTranslatedMessages(prev => {
        const newTranslations = { ...prev };
        delete newTranslations[messageId];
        return newTranslations;
      });
      return;
    }

    // Check if already translating
    if (translatingMessages.has(messageId)) {
      return;
    }

    // Get current language from i18n
    

    // Check WebSocket connection
    if (!wsManagerRef.current || !wsManagerRef.current.isReady()) {
      alert(t('Not connected. Please start the lesson first.'));
      return;
    }

    try {
      // Mark as translating
      setTranslatingMessages(prev => new Set(prev).add(messageId));

      // Store the message ID we're translating so we can match the response
      // We'll use a temporary storage in a ref
      if (!window.pendingTranslations) {
        window.pendingTranslations = new Map();
      }
      console.log('📝 Translating message:', messageId, 'Text:', messageText);
      window.pendingTranslations.set(messageText, messageId);

      // Request translation via WebSocket
      wsManagerRef.current.requestTranslation(messageText);

      // Note: The translation response will be handled in handleWebSocketMessage
      // with type 'ai.translation' or similar
    } catch (error) {
      console.error('Translation error:', error);
      alert(t('Translation failed. Please try again.'));
      
      // Remove from translating set
      setTranslatingMessages(prev => {
        const newSet = new Set(prev);
        newSet.delete(messageId);
        return newSet;
      });
    }
  };

  // Request question clarification (translation)
  const clarifyQuestion = () => {
    if (wsManagerRef.current && wsManagerRef.current.isReady()) {
      wsManagerRef.current.clarifyQuestion();
    }
  };

  // Skip correction repetition
  const skipCorrection = (messageId) => {
    if (wsManagerRef.current && wsManagerRef.current.isReady()) {
      wsManagerRef.current.skip();
      setSkippedCorrectionIds(prev => new Set([...prev, messageId]));
    }
  };

  const requestHint = () => {
    if (wsManagerRef.current && wsManagerRef.current.isReady()) {
      wsManagerRef.current.requestHint();
    }
  };

  // Struggle modal handlers
  const handleGoToVocab = () => {
    console.log('📚 Redirecting to vocabulary...');
    setShowStruggleModal(false);
    // Reset counters
    setConsecutiveErrors(0);
    setConsecutiveSilences(0);
    // Reset debounce timer
    lastErrorCheckTimeRef.current = 0;
    const choiceWraps = document.querySelectorAll('[data-choice-index]');
            choiceWraps.forEach((el) => {
              const index = parseInt(el.getAttribute('data-choice-index'));
              if (index === 1) {
                el.classList.add(css.active);
              } else {
                el.classList.remove(css.active);
              }
            });
    // Switch to vocabulary view
    if (window.lessonInfoHandlers) {
      window.lessonInfoHandlers.handleSelect('words');
    }
  };

  const handleGoToVideo = () => {
    console.log('🎥 Redirecting to video...');
    setShowStruggleModal(false);
    // Reset counters
    setConsecutiveErrors(0);
    setConsecutiveSilences(0);
    // Reset debounce timer
    lastErrorCheckTimeRef.current = 0;
    // Switch to video view
     const choiceWraps = document.querySelectorAll('[data-choice-index]');
            choiceWraps.forEach((el) => {
              const index = parseInt(el.getAttribute('data-choice-index'));
              if (index === 0) {
                el.classList.add(css.active);
              } else {
                el.classList.remove(css.active);
              }
            });
    if (window.lessonInfoHandlers) {
      window.lessonInfoHandlers.handleSelect('speak');
       
    }
  };

  const handleContinueDialogue = () => {
    console.log('💪 User chose to continue dialogue');
    setShowStruggleModal(false);
    // Reset counters to give user a fresh start
    setConsecutiveErrors(0);
    setConsecutiveSilences(0);
    // Reset debounce timer
    lastErrorCheckTimeRef.current = 0;
  };
  // --- Design 29 Logic ---
  const handleNextAiMessage = () => {
    setFeedbackBox(null);
    setUserAnswer(null);
    setIsWaitingForNext(false);
    isWaitingForNextRef.current = false;
    setSpeakingState('idle'); // Reset speaking state
    
    // Process buffered message
    if (nextAiMessageRef.current) {
      const data = nextAiMessageRef.current;
      nextAiMessageRef.current = null;
      setNextAiMessage(null);
      
      const aiTextMessage = {
        id: Date.now(),
        sender: 'ai',
        text: data.text,
        timestamp: new Date(),
        hasAudio: true
      };
      setMessages(prev => [...prev, aiTextMessage]);
      setLastAIQuestion(data.text);
      setShowUserHelp(true);
      setCurrentStep(data.step || currentStep);
    }
    
    // Play buffered audio chunks
    if (nextAiAudioChunksRef.current.length > 0) {
      nextAiAudioChunksRef.current.forEach(chunk => {
        handleAudioChunk(chunk);
      });
      nextAiAudioChunksRef.current = [];
    }
  };

  // End session
  const endSession = () => {
    // Send end session command to WebSocket
    if (wsManagerRef.current && wsManagerRef.current.isReady()) {
      wsManagerRef.current.endSession();
    }

    // Calculate total time including previous sessions
    const currentSessionDuration = sessionStartTime ? new Date() - sessionStartTime : 0;
    const finalTotalTime = totalTimeInvested + currentSessionDuration;

    // Update total time for display
    setTotalTimeInvested(finalTotalTime);

    // Close WebSocket connection
    if (wsManagerRef.current) {
      wsManagerRef.current.close();
      wsManagerRef.current = null;
    }

    setShowSummaryModal(true);
  };

  // Animation functions for results screen
  const animateScore = (target) => {
    const scoreNum = scoreNumberRef.current;
    const ring = scoreRingRef.current;
    if (!scoreNum || !ring) return;

    const circumference = 2 * Math.PI * 70;
    ring.style.strokeDasharray = circumference;
    ring.style.strokeDashoffset = circumference;

    setTimeout(() => {
      const offset = circumference - (circumference * target / 100);
      ring.style.strokeDashoffset = offset;
    }, 100);

    let current = 0;
    const duration = 1500;
    const step = target / (duration / 16);
    const startTime = performance.now();

    function tick(now) {
      const elapsed = now - startTime;
      current = Math.min(target, Math.round((elapsed / duration) * target));
      scoreNum.textContent = current;
      if (current < target) requestAnimationFrame(tick);
    }
    setTimeout(() => requestAnimationFrame(tick), 500);
  };

  const animateCounter = (el, target, delay) => {
    if (!el) return;
    setTimeout(() => {
      let current = 0;
      const interval = setInterval(() => {
        current++;
        el.textContent = current;
        if (current >= target) clearInterval(interval);
      }, 80);
    }, delay);
  };

  const spawnConfetti = () => {
    const container = confettiContainerRef.current;
    if (!container) return;
    const colors = ['#6C3AED', '#F97316', '#22C55E', '#60A5FA', '#FBBF24', '#EC4899'];
    for (let i = 0; i < 40; i++) {
      const piece = document.createElement('div');
      piece.className = 'finish-confetti';
      piece.style.left = Math.random() * 100 + '%';
      piece.style.top = '-10px';
      piece.style.background = colors[Math.floor(Math.random() * colors.length)];
      piece.style.animationDelay = (Math.random() * 1.2) + 's';
      piece.style.animationDuration = (2 + Math.random() * 2) + 's';
      piece.style.width = (5 + Math.random() * 7) + 'px';
      piece.style.height = (5 + Math.random() * 7) + 'px';
      piece.style.borderRadius = Math.random() > 0.5 ? '50%' : '2px';
      container.appendChild(piece);
    }
    setTimeout(() => { container.innerHTML = ''; }, 5000);
  };

  const setCompletionMessage = (score) => {
    // This will be handled in JSX based on score
  };

  // Effect to trigger animations when completion screen shows
  

  // Render message with highlighted vocabulary
  const renderMessageText = (text, sender, messageId) => {
    // Check if this message has a translation
    const isTranslated = !!translatedMessages[messageId];
    const displayText = translatedMessages[messageId] || text;
    
    if (sender === 'user') {
      let highlightedText = displayText;
      selectedTopic.vocabulary.forEach(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        if (regex.test(displayText)) {
          highlightedText = highlightedText.replace(regex, `<span class="highlighted-vocab">${word}</span>`);
        }
      });
      return <div dangerouslySetInnerHTML={{ __html: highlightedText }} />;
    }
    
    /// For AI messages, add prefix if translated
    if (isTranslated) {
      return (
        <div>
         {/*  <span className="translation-label" style={{ fontWeight: 'bold', marginRight: '5px' }}>
            {t('translation', 'translation')} :
          </span>*/}
          {displayText}
        </div>
      );
    }
    
    return <div>{displayText}</div>;
  };

  // Show loading state if lesson is not provided
  if (!lesson) {
    return (
      <div className="dialogues-container">
        {/* Background Shapes */}
        <div className="bg-shapes">
          <div className="shape"></div>
          <div className="shape"></div>
          <div className="shape"></div>
        </div>

        <div className="ready-screen">
          <div className="ready-content">
            <div className="ready-icon">
              <svg viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h2>{t("Loading...")}</h2>
            <p>{t("Please wait while we prepare your dialogue session.")}</p>
          </div>
        </div>
      </div>
    );
  }

  // Show connecting state
  if (isInitializing || wsStatus === 'connecting') {
    return (
      <div className="dialogues-container">
        {/* Background Shapes */}
        <div className="bg-shapes">
          <div className="shape"></div>
          <div className="shape"></div>
          <div className="shape"></div>
        </div>

        <div className="ready-screen">
          <div className="ready-content">
            <div className="ready-icon">
              <svg viewBox="0 0 24 24" style={{ animation: 'spin 1s linear infinite' }}>
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <h2>{t("Connecting...")}</h2>
            <p>{t("Please wait while we establish the connection.")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (showReadyScreen) {
    return (
      <div className="dialogues-container">
        {/* Background Shapes */}
        <div className="bg-shapes">
          <div className="shape"></div>
          <div className="shape"></div>
          <div className="shape"></div>
        </div>

        {/* Ready Screen */}
        <div className="ready-screen">
          <div className="ready-content">
            {errorContent ? (
              // Error State
              <>
                <div className="error-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
                  </svg>
                </div>
                <h2 className="error-title">{t(errorContent.title)}</h2>
                <p className="error-message">{t(errorContent.message)}</p>
                <div className="error-buttons">
                  <button className="ready-button error-button-reload" onClick={handleErrorAction}>
                    {t("Got it")}
                  </button>
                  <button className="ready-button error-button-back" onClick={goToPricing}>
                    {t("Pricing")}
                  </button>
                </div>
              </>
            ) : (
              // Success State
              <>
                <div className="ready-icon">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
                  </svg>
                </div>
                <h2>{t("Welcome to Dialogue Studio!")}</h2>
                <button className="ready-button" onClick={startLesson}>{t("Yes, I'm Ready!")}</button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dialogues-container">
      {/* Design 29 Top Bar - rendered via portal */}
      {typeof document !== 'undefined' && document.getElementById('dialogue-header-portal-target') && (
        createPortal(
          <div className="topbar" style={{
            gridColumn: '1/-1',
            background: '#fff',
            borderRadius: '16px',
            padding: '16px 24px',
            border: '1px solid rgba(0,0,0,.04)',
            boxShadow: '0 1px 8px rgba(0,0,0,.03)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '16px',
            maxWidth: '1400px',
            margin: '0 auto 16px',
            width: '100%'
          }}>
            <div className="tb-left" style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <div className="tb-step" style={{
                fontFamily: "'Sora',sans-serif", fontSize: '12px', fontWeight: 700,
                color: 'var(--i500)', background: 'var(--i50)', padding: '5px 14px',
                borderRadius: '100px', whiteSpace: 'nowrap'
              }}>
                {t('Step')} 4 {t('of')} 4
              </div>
              <div>
                <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: '16px', fontWeight: 700 }}>
                  {t('Practice Dialogue')}
                </h2>
                <p style={{ fontSize: '12.5px', color: 'var(--t3)', marginTop: '1px' }}>
                  {t('Repeat the phrases from the video. AI will check pronunciation and content.')}
                </p>
              </div>
            </div>
            <div className="tb-right" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontSize: '12px', fontWeight: 700, color: 'var(--i500)' }}>
                {t('Step')} {currentStep} {t('of')} {totalSteps || '?'}
              </div>
              <button className="btn-finish" style={{
                fontFamily: "'Sora',sans-serif", fontSize: '12px', fontWeight: 700,
                padding: '9px 20px', borderRadius: '10px', border: '1.5px solid var(--g200)',
                background: '#fff', color: 'var(--t2)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap'
              }} onClick={endSession}>
                {t('Finish lesson')}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </button>
            </div>
          </div>,
          document.getElementById('dialogue-header-portal-target')
        )
      )}

      {/* Main Card */}
      <div className="mc" style={{
        background: '#fff', borderRadius: '20px', border: '1px solid rgba(0,0,0,.04)',
        boxShadow: '0 2px 16px rgba(0,0,0,.03)', minHeight: '520px',
        display: 'flex', flexDirection: 'column'
      }}>
        {!showCompletion ? (
          <>
            {/* Progress Bar */}
            <div className="sp-prog">
              <div className="sp-counter">
                {t('Step')} {currentStep} {t('of')} {totalSteps || '?'}
              </div>
              <div className="sp-bar">
                <div className="sp-bar-fill" style={{ width: totalSteps ? `${(currentStep / totalSteps) * 100}%` : '10%' }}></div>
              </div>
              <div className="sp-scores">
                <div className="sp-sc ok">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                  <span>{turnCount}</span>
                </div>
              </div>
            </div>

            {/* AI Section */}
            <div className="ai-sec">
              <div className="ai-top">
                <div className="ai-label">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.9 5.8a2 2 0 0 1-1.3 1.3L3 12l5.8 1.9a2 2 0 0 1 1.3 1.3L12 21l1.9-5.8a2 2 0 0 1 1.3-1.3L21 12l-5.8-1.9a2 2 0 0 1-1.3-1.3L12 3Z"/></svg>
                  {t('AI says')}
                </div>
                <div className="ai-controls">
                  {/* Replay */}
                  <div className="ai-ic" onClick={() => {
                    const lastAiMsg = [...messages].reverse().find(m => m.sender === 'ai' && !m.isCorrection);
                    if (lastAiMsg) replayAudio(lastAiMsg.id);
                  }}>
                    <span className="tip">{t('Replay')}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>
                  </div>
                  {/* Slow */}
                  <div className="ai-ic" onClick={() => {
                    const lastAiMsg = [...messages].reverse().find(m => m.sender === 'ai' && !m.isCorrection);
                    if (lastAiMsg) replayAudioSlowly(lastAiMsg.id);
                  }}>
                    <span className="tip">{t('Slower')}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  </div>
                  {/* Translate */}
                  <div className="ai-ic" onClick={() => {
                    const lastAiMsg = [...messages].reverse().find(m => m.sender === 'ai' && !m.isCorrection);
                    if (lastAiMsg) translateMessage(lastAiMsg.id, lastAiMsg.text);
                  }}>
                    <span className="tip">{t('Translate')}</span>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                  </div>
                </div>
              </div>

              {/* Chat Bubbles */}
              <div className="chat-area">
                {/* AI bubble — show last AI message */}
                {(() => {
                  const lastAiMsg = [...messages].reverse().find(m => m.sender === 'ai' && !m.isCorrection);
                  return lastAiMsg ? (
                    <div className="chat-bubble chat-ai">{lastAiMsg.text}</div>
                  ) : (
                    <div className="chat-bubble chat-ai" style={{ color: 'var(--t3)' }}>
                      {wsStatus === 'connecting' || isInitializing ? t('Connecting...') : t('Waiting for AI...')}
                    </div>
                  );
                })()}

                {/* User bubble */}
                {userAnswer && (
                  <div className="chat-bubble chat-user sh">{userAnswer}</div>
                )}
              </div>

              {/* Translation */}
              {(() => {
                const lastAiMsg = [...messages].reverse().find(m => m.sender === 'ai' && !m.isCorrection);
                if (lastAiMsg && translatedMessages[lastAiMsg.id]) {
                  return (
                    <div className="ai-trans sh">
                      {translatedMessages[lastAiMsg.id]}
                    </div>
                  );
                }
                return null;
              })()}
            </div>

            <div className="sec-div"></div>

            {/* User Section */}
            <div className="usr-sec">
              <div className="usr-top">
                <div className="usr-label">{t('Your answer')}</div>
                <div className="usr-help">
                  <button className="uh" onClick={() => {
                    if (!helpExpanded) {
                      requestHint();
                    } else {
                      setHelpExpanded(false);
                    }
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
                    {t('Hint')}
                  </button>
                  <button className="uh" onClick={() => {
                    if (wsManagerRef.current && wsManagerRef.current.isReady()) {
                      skipCorrection(lastCorrectionMessageIdRef.current);
                    }
                  }}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 7-7 7 7"/></svg>
                    {t('Skip')}
                  </button>
                </div>
              </div>

              {/* Idle State */}
              {!isRecording && !recordedAudioBlob && !feedbackBox && (
                <div className="rs rs-idle on">
                  <button className="mic-btn"
                    onClick={handleStartRecording}
                    disabled={isSendingMessage || !wsManagerRef.current || !wsManagerRef.current.isReady()}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"/>
                      <path d="M19 10v2a7 7 0 0 1-14 0v-2"/>
                    </svg>
                  </button>
                  <div className="mic-info">
                    <div className="mic-title">{isSendingMessage ? t('Processing...') : t('Record answer')}</div>
                    <div className="mic-sub">{t('Click to start recording')}</div>
                  </div>
                </div>
              )}

              {/* Recording State */}
              {isRecording && (
                <div className="rs rs-rec on">
                  <div className="rec-indicator"><span className="rec-dot"></span>{t('Recording...')}</div>
                  <div className="rec-sub">{t('Speak now')}</div>
                  <div className="rec-wave">
                    {Array(8).fill(0).map((_, i) => <div key={i} className="rec-wb"></div>)}
                  </div>
                  <button className="stop-btn" onClick={handleStopRecording}>
                    <svg viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="1"/></svg>
                    {t('Stop')}
                  </button>
                </div>
              )}

              {/* Preview State */}
              {!isRecording && recordedAudioBlob && !feedbackBox && (
                <div className="rs rs-preview on">
                  <div className="prev-title">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
                    {t('Your recording is ready')}
                  </div>
                  <div className="prev-wave">
                    {audioWaveformData.length > 0
                      ? audioWaveformData.slice(0, 14).map((amp, i) => (
                          <div key={i} className="prev-bar" style={{ height: `${Math.max(amp * 100, 20)}%` }}></div>
                        ))
                      : Array(14).fill(0).map((_, i) => <div key={i} className="prev-bar"></div>)
                    }
                  </div>
                  <div className="prev-actions">
                    <div className="pa-ic" onClick={playRecordedAudio}>
                      <span className="tip">{t('Listen')}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                    </div>
                    <div className="pa-ic" onClick={() => { deleteRecordedAudio(); }}>
                      <span className="tip">{t('Re-record')}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/></svg>
                    </div>
                    <div className="pa-ic del" onClick={deleteRecordedAudio}>
                      <span className="tip">{t('Delete')}</span>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/></svg>
                    </div>
                    <button className="send-btn" onClick={sendRecordedAudio} disabled={isSendingMessage}>
                      {t('Send')}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Hint Box */}
            {helpExpanded && (
              <div className="hint sh">
                💡 {t('Try saying:')} <strong>"{getContextualExample()}"</strong>
              </div>
            )}

            {/* Feedback Box */}
            {feedbackBox && (
              <div className={`fb sh ${feedbackBox.type === 'correct' ? 'ok' : 'no'}`}>
                <div className="fb-row">
                  <div className="fb-left">
                    <div className="fb-t">
                      {feedbackBox.type === 'correct' ? `${t('Accepted')} ✓` : t('Almost correct')}
                    </div>
                    <div className="fb-d">
                      {feedbackBox.type === 'correct'
                        ? t('Your answer sounds good.')
                        : t('Try again or skip.')
                      }
                    </div>
                    {feedbackBox.type === 'wrong' && feedbackBox.expected && (
                      <div className="fb-cor">{t('Correct')}: "{feedbackBox.expected}"</div>
                    )}
                  </div>
                  {feedbackBox.type === 'correct' ? (
                    <button className="fb-btn fb-btn-p" onClick={handleNextAiMessage}>
                      {t('Next')}
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                    </button>
                  ) : (
                    <div className="fb-btns">
                      <button className="fb-btn fb-btn-s" onClick={() => {
                        setFeedbackBox(null);
                        setUserAnswer(null);
                        setRecordedAudioBlob(null);
                      }}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/></svg>
                        {t('Retry')}
                      </button>
                      <button className="fb-btn fb-btn-p" onClick={handleNextAiMessage}>
                        {t('Skip')}
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 7-7 7 7"/></svg>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mc-pad"></div>
          </>
        ) : (
          <NotCompletionScreen
            lesson={lesson}
          />
        )}
      </div>

      {/* Summary Modal */}
      {showSummaryModal && (
        <div className="modal-overlay" style={{ display: 'flex' }}>
          <div className="modal-content">
            <div className="modal-header">
              <div className="trophy-icon">
                <svg viewBox="0 0 24 24">
                  <path d="M5 16L3 5l5.5 0m4.5 0H21l-2 11m-2 0H7m10 0l-1 3m-9 0l1 3m5-3h-4m2-7a1 1 0 110 2 1 1 0 010-2z" />
                </svg>
              </div>
              <h2>{t("Brilliant Session!")}</h2>
              <p>{t("You've created something beautiful with words today")}</p>
            </div>

            <div className="summary-stats">
              <div className="stat-card">
                <div className="stat-value">
                  {(() => {
                    const currentSessionDuration = sessionStartTime ? new Date() - sessionStartTime : 0;
                    const finalTotalTime = totalTimeInvested + currentSessionDuration;
                    const minutes = Math.floor(finalTotalTime / 60000);
                    const seconds = Math.floor((finalTotalTime % 60000) / 1000);
                    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
                  })()}
                </div>
                <div className="stat-label">{t("Time Invested")}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{turnCount}</div>
                <div className="stat-label">{t("Exchanges")}</div>
              </div>
            </div>

            <div className="feedback-section">
              <h3 className="feedback-title">{t("Your Achievements")}</h3>
              <div className="feedback-list">
                {usedVocabulary.size === selectedTopic.vocabulary.length && (
                  <div className="feedback-item">
                    <svg className="feedback-icon good" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                    <span>{t("Perfect! You used all 5 vocabulary words brilliantly!")}</span>
                  </div>
                )}
                {turnCount >= 10 && (
                  <div className="feedback-item">
                    <svg className="feedback-icon good" width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" />
                    </svg>
                    <span>Excellent conversation flow! You maintained an engaging dialogue.</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button
                className="modal-button primary"
                onClick={() => {
                  sessionStorage.setItem("fromOnboarding", "true");
                  navigate("/", { replace: true });
                }}
              >
                {t("New Adventure")}
              </button>
              <button
                className="modal-button secondary"
                onClick={() => setShowSummaryModal(false)}
              >
                {t("Exit")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Struggle Support Modal */}
      <StruggleSupportModal
        isVisible={showStruggleModal}
        variant={struggleVariant}
        onClose={() => setShowStruggleModal(false)}
        onGoToVocab={handleGoToVocab}
        onGoToVideo={handleGoToVideo}
        onContinue={handleContinueDialogue}
      />
    </div>
  );
};

export default Dialogues;
